/*
 *   File : competition-overview.js
 *   Author : https://evoqins.com
 *   Description : Competition detail page
 *   Integrations : react-toastify
 *   Version : 1.0.0
*/

// Package
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal } from 'bootstrap'
import { toast } from "react-toastify";

// Custom components
import { CompetitionCard, StatisticCard } from "../../../Component/Cards";
import { ConfirmDeleteModal, ManageCompetitionModal } from "../../../Component/Modal";
import { APIService } from "../../../Service";
import { Loader } from "../../../Component/Others";
import { EmptyScreen } from "../../../Component/EmptyComponent";
import { useSelector } from "react-redux";
import { TextButton } from "../../../Component/Buttons";
import { DataTableComponent } from "../../../Component/DataTable";
import { _isUaeRegion } from "../../../Helper/country";

const ITEMS_PER_PAGE = 20;

const CampaignOverview = () => {

    const scrollRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { state } = useLocation();

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);


    const [confirmDelete, setConfirmDelete] = useState(false);
    const [competitionId, setCompetitionId] = useState(null);
    const [competitionDetail, setCompetitionDetail] = useState({});
    const [loading, setLoading] = useState(true);
    const [deleted, setDeleted] = useState(false);

    const [productList, setProductList] = useState([]);
    const [productLoading, setProductLoading] = useState(true);
    const [competitionModal, setCompetitionModal] = useState(false);

    const [leaderBoardData, setLeaderBoardData] = useState({});
    const [leaderBoardLoading, setLeaderBoardLoading] = useState(true);
    const [btnLoader, setBtnLoader] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);


    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
        setBtnLoader(false);
    }, []);

    useEffect(() => {
        if (confirmDelete === true) {
            const modal = new Modal(document.getElementById("confirm-delete-modal"));
            modal.show()
        }
    }, [confirmDelete]);

    useEffect(() => {
        if (location.state !== null) {
            setCompetitionId(location.state.competition_id);
        } else {
            navigate("/campaign");
        }
    }, [location]);

    useEffect(() => {
        if (competitionId !== null) {
            _competitionDetail();
            _leaderBoardData();
        }
    }, [competitionId]);

    useEffect(() => {
        if (competitionModal === true) {
            const manage_modal = new Modal(document.getElementById('manage-competition-modal'));
            manage_modal.show();
        }
    }, [competitionModal]);

    // handle edit competition modal 
    const _handleEdit = () => {
        setCompetitionModal(true);
        _getProducts();
    };

    // handle delete competition
    const _handleDelete = () => {
        setConfirmDelete(true);
    };

    // handle success edit 
    const _handleSuccessEdit = () => {
        setLoading(true);
        _competitionDetail();
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    };

    // handle paginate store list 
    const _handlePageChange = (page_num) => {
        setCurrentPage(page_num);

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // handle send notification 
    const _handleSendNotification = (id) => {
        setBtnLoader(true);
        _sendNotification();
    };

    // API - Get competition detail
    const _competitionDetail = () => {
        let is_post = true;
        let url = 'CA/competition/detail';
        let data = {
            "competition_id": competitionId
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setCompetitionDetail(response.data);
            } else {

            }
            setLoading(false);
        });
    };

    // API - Delete competition
    const _deleteCompetition = () => {
        let is_post = true;
        let url = 'CA/competition/delete';
        let data = {
            "competition_id": competitionId
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setDeleted(true);
            } else {
                setDeleted(false);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
        });
    };

    // API - get products
    const _getProducts = () => {
        let is_post = true;
        let url = 'general/products';
        let data = {
            "query": "",
            "page_num": 1
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setProductList(response.data);
            } else {
            }
            setProductLoading(false);
        });
    };

    // API - Get leader board data
    const _leaderBoardData = () => {
        let is_post = true;
        let url = 'CA/competition/leaderboard';
        let data = {
            "competition_id": competitionId,
            "page_num": 1
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setLeaderBoardData(response.data);
            } else {
                setLeaderBoardData({});
            }
            setLeaderBoardLoading(false);
        });
    };

    // API - Send notification
    const _sendNotification = () => {
        let is_post = true;
        let url = 'CA/competition/send-notification';
        let data = {
            "competition_id": competitionId
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("Notification send successfully", {
                    type: "success",
                    className: "e-toast",
                });
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setBtnLoader(false);
        });
    };

    const TABLE_COLUMNS = [
        {
            name: 'Participant name',
            selector: row => row.name || "-",
            cell: row => <>{row.name || "-"}</>,
            width: "280px",
            sortable: true
        },
        {
            name: 'Sales unit',
            selector: row => row.sales_unit || "-",
            width: "120px",
            sortable: true
        },
        ...(!_isUaeRegion(PROFILE_DATA)
            ? [{
                name: 'Sales amount',
                selector: row => row.sales_amount || "-",
                width: "120px",
                sortable: true
            }]
            : []),
        {
            name: 'Points awarded',
            selector: row => row.points || "-",
            width: "150px",
            sortable: true
        },
        {
            name: 'Progress',
            selector: row => row.progress_text || "-",
            width: "100px",
            sortable: true
        },
        {
            name: 'Status',
            selector: row => row.status,
            cell: row => <>
                {row.status ?
                    <span className={`${row.status.toLowerCase() === "Completed" ? "e-text-philippine-green" : "e-text-brilliant-blue "}  e-font-weight-500`}>{row.status}</span>
                    : "-"}
            </>,
            width: "100px"
        },
    ];

    return (
        <Fragment>
            <section className='pb-5 '>
                <div className='e-bg-lavender-blush-light padding-12px-top padding-88px-bottom padding-52px-lr'>
                    <div className='d-flex justify-content-space-between flex-wrap align-items-center'>
                        <div className='e-display-flex w-max-content cursor-pointer'
                            onClick={() => navigate("/campaign", {
                                state: {
                                    tab_index: state?.tab_index || 1,
                                }
                            })} >
                            <img src={require("../../../Assets/Image/CountryAdmin/arrow-left.svg").default}
                                draggable={false}
                                className='cursor-pointer'
                                alt='arrow'
                            />
                            <h1 className='ps-1 mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                                Campaign Overview
                            </h1>
                        </div>
                        {/* <SecondaryButton name="Send notification"
                            className="py-2 e-border-radius-16 mt-xl-0 mt-lg-0 mt-md-0 mt-sm-0 mt-2 w-148px"
                            status={btnLoader}
                            onPress={_handleSendNotification} /> */}
                    </div>

                </div>
                <div className="padding-52px-lr margin-neg-56px-top">
                    <div className='row gx-3 row-gap-3 mb-4 row-cols-xl-5 row-cols-lg-4 row-cols-md-4 row-cols-2'>
                        <div className='col m-0'>
                            <StatisticCard
                                loader={false}
                                icon={require("../../../Assets/Image/CountryAdmin/users.svg").default}
                                quantity={competitionDetail?.eligible_participants_text || "-"}
                                title={"Eligible participants"}
                                className="e-border-radius-8 p-3" />
                        </div>

                        <div className='col m-0'>
                            <StatisticCard
                                loader={false}
                                icon={require("../../../Assets/Image/CountryAdmin/ManageCompetition/active-participant.svg").default}
                                quantity={competitionDetail?.active_participants_text || "-"}
                                title={"Active participants"}
                                className="e-border-radius-8 p-3" />
                        </div>

                        <div className='col m-0'>
                            <StatisticCard
                                loader={false}
                                icon={require("../../../Assets/Image/CountryAdmin/ManageCompetition/target-achievement.svg").default}
                                quantity={competitionDetail?.completed_percentage_text || "-"}
                                title={"Target achievement"}
                                className="e-border-radius-8 p-3" />
                        </div>
                    </div>
                    {loading ?
                        <Loader />
                        :
                        <>
                            <div className="pb-4" >
                                <CompetitionCard
                                    data={{
                                        ...competitionDetail,
                                        users_data: {
                                            country: competitionDetail?.country_name,
                                            area: competitionDetail?.area_name,
                                            store: competitionDetail?.store_name,
                                        }
                                    }}

                                    detail={true}
                                    handleEdit={_handleEdit}
                                    handleConfirm={_handleDelete} />
                            </div>
                            <div >
                                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                                    <h2 className=' mb-0 e-primary-font-playfair e-font-18 line-height-28px e-text-dark-jungle-green e-font-weight-500'>
                                        Top 10 Live Leaderboard
                                    </h2>
                                    <TextButton name="View full leaderboard →"
                                        onPress={() => {
                                            navigate("/campaign/detail", {
                                                state: {
                                                    ...state,
                                                    id: competitionId
                                                }
                                            })
                                        }} />
                                </div>
                                <div className='e-bg-white e-admin-box-shadow py-3 e-border-radius-16 '>
                                    {leaderBoardLoading ?
                                        <Loader className="h-40vh" />  //loader
                                        :
                                        <>
                                            {Object.keys(leaderBoardData).length !== 0 && leaderBoardData.data?.length !== 0 ?
                                                <div ref={scrollRef} className={`e-table  no-hover e-store-table`}>
                                                    <DataTableComponent columns={TABLE_COLUMNS}
                                                        pagination={true}
                                                        data={leaderBoardData.data}
                                                        paginationPerPage={ITEMS_PER_PAGE}
                                                        paginationServer={true}
                                                        paginationTotalRows={leaderBoardData.total_count}
                                                        paginationDefaultPage={currentPage}
                                                        onChangePage={_handlePageChange} />
                                                </div>
                                                :
                                                <div className='min-vh-100 py-5 d-flex flex-column justify-content-center'>
                                                    <EmptyScreen message="No list found" />
                                                </div>
                                            }
                                        </>
                                    }
                                </div>
                            </div>
                        </>
                    }
                </div>
            </section>

            {/* Manage competition modal */}
            {competitionModal === true &&
                <ManageCompetitionModal product={productList.data}
                    edit={true}
                    detailData={competitionDetail}
                    loading={productLoading}
                    success={() => _handleSuccessEdit()}
                    handleClose={() => {
                        setTimeout(() => {
                            setCompetitionModal(false);
                        }, 200);
                    }} />
            }

            {/* Delete confirm modal */}
            {confirmDelete === true &&
                <ConfirmDeleteModal label="competition"
                    handleClose={() => setConfirmDelete(false)}
                    response={deleted}
                    delete={() => _deleteCompetition()}
                    success={() => {
                        setConfirmDelete(false);
                        navigate("/campaign")
                    }} />}
        </Fragment>
    )
}

export default CampaignOverview;